import { Dialog, DialogActions, DialogContent } from '@mui/material';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import ActionButton from 'Components/ActionButton';
import HistoryRowBasicItem from 'Components/HistoryRowBasicItem';
import colors from '../../../../../config/theme/colors';
import QuestionStepper from 'Components/QuestionStepper';
import { getStepContent, steps } from '../updrsIIISettings';
import { getStepContent as getStepContentOld, steps as stepsOld } from '../updrsIIIOldSettings';
import { actions as actionsD, TCreationPromiseType } from 'Store/documents/actions';
import { actions } from 'Store/form';
import InputHandler from 'Components/InputHandler';
import FormRow from 'Components/FormRow';
import {
  mdsUpdrsIIITestDescription,
  mdsUpdrsIIITestReferenceInfo,
  dyskinesiaQuestions,
  updrsIIITestReferenceInfo,
  StyledScore,
  StyledCancel,
  StyledUPDRSArea,
  scoreText,
  notFilledText,
  IEditingDocument,
} from './dialogComponents';
import InfoPopover from 'Components/InfoPopover';
import { omitControlProps } from 'Utility/documentHandling';
import { Container, Item } from 'Components/Grid';

import { updrs_iii } from 'neuro-schemas';

type Props = IOwnProps & IDispatchFromProps;

/** Dialog form buffer: data fields are filled in gradually, control props only exist once the document is created */
type TUpdrsIIIFormData = Omit<IDocumentWithTypeV2<'updrs_iii'>, keyof IControlProps> &
  Partial<IControlProps<'updrs_iii'>>;

class UPDRSDialog extends React.Component<Props, IOwnState> {
  private dialogContent: HTMLElement | null;
  private dialogContentRef: (element: HTMLElement | null) => void;
  constructor(props: Props) {
    super(props);
    // deepcode ignore ReactStateFromProps: Needs rework to fix
    this.state = {
      formData: props.document ?? undefined,
      previousFormData: undefined,
      open: false,
      id: props.document?._id,
    };
    this.dialogContent = null;
    this.dialogContentRef = (element: HTMLElement | null): void => {
      this.dialogContent = element;
    };
  }

  // Use this to get retrospective checkbox status from beforeDoc and save it to afterDoc
  private updateRetroStatus = (): void => {
    if (this.props.retro !== this.state.formData?.retrospectiveData?.[0] && this.props.retro !== undefined) {
      this.setState({ formData: { retrospectiveData: [this.props.retro], ...this.state.formData } });
      this.onChange({ retrospectiveData: [this.props.retro] });
    }
  };

  /**
   * This function is called automatically if UPDRSIII dialog is updated in any way
   */
  public componentDidUpdate(): void {
    // Load form data
    if (this.state.formData && !this.state.formData._id && this.props.document) {
      this.setState({ formData: { ...this.state.formData, ...this.props.document } });
    }
  }

  public newCommitOrDocument = (id: string): void => {
    if (id && id !== 'noid') {
      // Create new commit for existing document
      const makeCommit = async (): Promise<TCreationPromiseType> =>
        await this.props.createCommit({ id, name: 'updrs_iii' }, this.props.document);
      makeCommit()
        .then((res: TCreationPromiseType) => {
          if (typeof res === 'object' && res._cid) {
            this.setState({
              id,
              formData: this.props.document,
              previousFormData: this.props.document,
            });
            this.updateRetroStatus();
          } else if (res === 'Commit creation failed') {
            this.setState({ open: false });
          }
        })
        .catch(() => {
          this.setState({ open: false });
        });
    } else if (id === 'noid') {
      // Create new document
      const makeDocument = async (): Promise<TCreationPromiseType> =>
        await this.props.createDocument('updrs_iii', true);
      // ID for new document received
      makeDocument()
        .then((res: TCreationPromiseType) => {
          // Link ID to levodopatest|dbsEffect
          if (typeof res === 'object' && res._id && this.props.onChange) {
            // file deepcode ignore ReactNextState: Needs rework to fix
            this.setState({ id: res._id });

            // Save this id to parent doc
            this.props.onChange({ [this.props.name]: res._id });

            const initValues: { [key: string]: PartialDate | string | boolean[] } = {};

            // Set date and type for updrs automatically
            if (this.props.parentDate?.[0]) initValues.date = this.props.parentDate;
            if (this.props.retro !== undefined) initValues.retrospectiveData = [this.props.retro];

            initValues.testType = this.props.testType ?? 'MDSUPDRS';
            initValues.type = this.props.context;

            this.onChange(initValues, res._id);
            //this.updateRetroStatus();
          }
        })
        .catch(() => {
          return;
        });
    } else {
      this.setState({ id, formData: this.props.document });
    }
  };

  public openDialog = (id: string) => (): void => {
    this.setState({ open: true });
    !this.props.viewing && this.newCommitOrDocument(id);
  };

  public closeDialog =
    (cancel = false) =>
    (): void => {
      if (this.state.id && !this.props.viewing) {
        if (!cancel) {
          this.props.updateDocument(
            { id: this.state.id, name: 'updrs_iii' },
            omitControlProps(this.state.formData ?? {}),
            true,
          );
          // Close commit when saving main document instead (in FormEditingHandler)
        } else {
          const id = this.state.id;
          if (this.state.formData === undefined || !this.props.document) {
            this.props.deleteDocument({ id: id, name: 'updrs_iii' }, { reason: 'Document creation canceled' });
          } else {
            if (this.state.previousFormData === undefined) {
              this.props.deleteCommit({ id: id, name: 'updrs_iii' });
            } else {
              this.props.updateDocument(
                { id: this.state.id, name: 'updrs_iii' },
                omitControlProps(this.state.previousFormData),
                true,
              );
            }
            this.props.clearFormValues(id);
            this.props.unsetEditing(id);
          }
        }
        this.setState({ id: undefined, formData: {} });
      }
      this.setState({ open: false });
    };

  public onChange = (values: TOnChangeValues, newId?: string): void => {
    const id = newId ?? this.state.id;
    if (id && id !== 'noid') {
      this.setState({ formData: { ...this.state.formData, ...values } });
      this.props.updateDocument(
        { id: id, name: 'updrs_iii' },
        omitControlProps({
          ...this.state.formData,
          ...values,
        }), // Remove control props again
      );
    }
  };

  /**
   * This function is called automatically when UPDRSIII dialog
   * is mounted / created.
   *    E.G: Page has been refreshed by the browser.
   */
  public componentDidMount() {
    if (this.props.document) {
      const id = this.props.document._id;
      if (this.props.document._editing === true) {
        this.props.loadFormValues(this.props.document);
        this.openDialog(id)();
      }
    }
  }

  public render(): React.JSX.Element {
    const score = this.props.document && updrs_iii.calculateScore(this.props.document);
    return (
      <React.Fragment>
        {/* Dialog page */}
        <Dialog open={this.state.open} maxWidth="lg" fullWidth={true} PaperProps={{ square: true }}>
          {/* Top field with 'retro data' - checkbox */}
          <DialogContent ref={this.dialogContentRef} style={{ padding: '3rem', fontSize: '1.6rem' }}>
            {this.state.id && this.state.id !== 'noid' ? (
              <React.Fragment>
                {this.props.testType === 'UPDRS' && (
                  <FormRow title="updrs.totalScoreEntry">
                    <InputHandler
                      type="Checkbox"
                      editing={!this.props.viewing}
                      name="retrospectiveData"
                      formData={{
                        document:
                          this.props.retro || this.props.retro === false
                            ? { retrospectiveData: [this.props.retro] }
                            : (this.state.formData ?? {}),
                        onChange: this.props.retro || this.props.retro === false ? undefined : this.onChange,
                      }}
                      preset="yesForceEn"
                      disabled={this.props.retro === true || this.props.retro === false}
                    />
                  </FormRow>
                )}

                {/* MDS - UPRDSIII */}
                {this.state.formData?.retrospectiveData?.[0] !== true && (
                  <div>
                    <div style={{ color: colors.primary, fontSize: '2.0rem', fontWeight: 600 }}>
                      <FormattedMessage id={`updrs.${this.props.testType === 'UPDRS' ? 'updrsIII' : 'mds'}`} />
                    </div>
                    {this.props.testType !== 'UPDRS' ? (
                      mdsUpdrsIIITestDescription
                    ) : (
                      <div style={{ margin: '0rem 0 1rem 0' }} />
                    )}
                    {this.props.testType === 'UPDRS' ? (
                      <QuestionStepper
                        formData={{
                          document: this.state.formData ?? {},
                          onChange: this.onChange,
                        }}
                        steps={stepsOld}
                        pageType={'updrs'}
                        docType={'iii'}
                        getStepContent={getStepContentOld}
                        viewing={this.props.viewing}
                        parent={this.dialogContent}
                      />
                    ) : (
                      <QuestionStepper
                        formData={{
                          document: this.state.formData ?? {},
                          onChange: this.onChange,
                        }}
                        steps={steps}
                        pageType={'updrs'}
                        docType={'iii'}
                        getStepContent={getStepContent}
                        viewing={this.props.viewing}
                        parent={this.dialogContent}
                      />
                    )}
                    <div>
                      {this.props.testType !== 'UPDRS' &&
                        dyskinesiaQuestions(!this.props.viewing, {
                          onChange: this.onChange,
                          document: this.state.formData ?? {},
                        })}
                    </div>
                    <div>
                      {this.props.testType === 'UPDRS' ? updrsIIITestReferenceInfo : mdsUpdrsIIITestReferenceInfo}
                    </div>
                  </div>
                )}
                {/* UPRDSIII */}
                {this.state.formData?.retrospectiveData?.[0] === true && (
                  <React.Fragment>
                    <div style={{ color: colors.primary, fontSize: '2.0rem', fontWeight: 600, margin: '0 0 1rem 0' }}>
                      <FormattedMessage id="updrs.updrsIII" />
                    </div>
                    {!this.props.viewing && (
                      <FormRow title="updrs.manualScoreEng">
                        <InputHandler
                          type="NumberField"
                          editing={!this.props.viewing}
                          name="manualScore"
                          formData={{
                            document: this.state.formData ?? {},
                            onChange: this.onChange,
                          }}
                          placeholder="updrs.scoreManual"
                          min={0}
                          max={108}
                          width={12.6}
                          height={3}
                          maxLength={3}
                        />
                      </FormRow>
                    )}
                    {updrsIIITestReferenceInfo}
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : (
              <FormattedMessage id="updrs.waitForID" />
            )}
          </DialogContent>
          {/* DialogAction field... */}
          <DialogActions
            style={{
              padding: '3rem 5rem',
            }}
          >
            <Container justifyContent="flex-end" alignItems="center">
              {!this.props.viewing ? (
                <React.Fragment>
                  {this.state.id && this.state.id !== 'noid' ? (
                    <React.Fragment>
                      <Item xs={6}>
                        {/* ...with save and cancel buttons */}
                        {this.state.formData?.retrospectiveData?.[0] !== true && (
                          <Container>
                            <Item xs={3}>{scoreText}</Item>
                            <Item xs={9}>
                              <StyledScore>
                                {typeof score === 'number' ? (
                                  score
                                ) : (
                                  <InfoPopover text="general.notFilled" color="primary" />
                                )}
                              </StyledScore>
                            </Item>
                          </Container>
                        )}
                      </Item>
                      <Item xs={6}>
                        <Container justifyContent="flex-end" alignItems="center">
                          <Item>
                            <StyledCancel onClick={this.closeDialog(true)}>
                              <FormattedMessage id="general.cancel" />
                            </StyledCancel>
                          </Item>
                          <Item>
                            <ActionButton
                              text="general.accept"
                              onClick={this.closeDialog()}
                              width={12}
                              height={3}
                              fontSize={16}
                            />
                          </Item>
                        </Container>
                      </Item>
                    </React.Fragment>
                  ) : undefined}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {/* ...with close button */}
                  <Container>
                    <Item xs={10}>
                      {typeof score === 'number' ? (
                        <Container>
                          <Item xs={2}>{scoreText}</Item>
                          <Item xs={10}>
                            <StyledScore>{score}</StyledScore>
                          </Item>
                        </Container>
                      ) : (
                        <StyledScore>{notFilledText}</StyledScore>
                      )}
                    </Item>
                    <Item xs={2}>
                      <Container justifyContent="flex-end">
                        <Item>
                          <ActionButton
                            text="general.close"
                            onClick={this.closeDialog(true)}
                            width={12}
                            height={3}
                            fontSize={16}
                          />
                        </Item>
                      </Container>
                    </Item>
                  </Container>
                </React.Fragment>
              )}
            </Container>
          </DialogActions>
        </Dialog>

        {/* Dialog fields of Levodopa and DBS-Effect test forms */}
        {this.state.formData === undefined || !this.props.document ? (
          <StyledUPDRSArea filled={!!this.state.formData}>
            <Container justifyContent="flex-end" alignItems="center">
              <Item xs={8}>{notFilledText}</Item>
              <Item xs={4}>
                <Container justifyContent="flex-end">
                  <Item>
                    {!this.props.viewing && (
                      <ActionButton
                        text="updrs.newUpdrsIII"
                        onClick={this.openDialog('noid')}
                        width={13}
                        height={3}
                        fontSize={16}
                      />
                    )}
                  </Item>
                </Container>
              </Item>
            </Container>
          </StyledUPDRSArea>
        ) : (
          <StyledUPDRSArea filled={!!this.state.formData}>
            <Container alignItems="center">
              <Item xs={8}>
                {typeof score === 'number' ? (
                  <HistoryRowBasicItem
                    header={
                      this.props.document.retrospectiveData?.[0] !== true ? (
                        <FormattedMessage
                          id={`updrs.${this.props.testType === 'UPDRS' ? 'scoreManual' : 'scoreMDS'}`}
                        />
                      ) : (
                        <FormattedMessage id="updrs.scoreManual" />
                      )
                    }
                    value={`${score}`}
                  />
                ) : (
                  <FormattedMessage id="updrs.notFilled" />
                )}
              </Item>
              <Item xs={4}>
                <Container justifyContent="flex-end">
                  <Item>
                    <ActionButton
                      text={!this.props.viewing ? 'general.edit' : 'general.view'}
                      onClick={this.openDialog(this.props.document._id)}
                      width={13}
                      height={3}
                      fontSize={16}
                    />
                  </Item>
                </Container>
              </Item>
            </Container>
          </StyledUPDRSArea>
        )}
      </React.Fragment>
    );
  }
}

interface IOwnState {
  formData?: TUpdrsIIIFormData;
  previousFormData?: TUpdrsIIIFormData;
  open: boolean;
  id?: string;
}

interface IDispatchFromProps {
  unsetEditing: (id: string) => void;
  clearFormValues: (id: string) => void;
  loadFormValues: (document: { _id: string }) => void;
  createDocument: (name: string, noFormUpdate?: boolean) => Promise<TCreationPromiseType>;
  createCommit: (editingDocument: IEditingDocument, oldData?: IControlProps) => Promise<TCreationPromiseType>;
  updateDocument: (
    editingDocument: IEditingDocument,
    formData?: TAnyObject,
    noFormUpdate?: boolean,
  ) => Promise<TCreationPromiseType>;
  closeCommit: (editingDocument: IEditingDocument, formData?: TAnyObject) => void;
  deleteCommit: (editingDocument: IEditingDocument) => void;
  deleteDocument: (editingDocument: IEditingDocument, reason: { [key: string]: string | PartialDate }) => void;
}

const mapDispatchToProps = (dispatch: any): any => ({
  loadFormValues: (document: TAnyObject & IControlProps): void => dispatch(actions.checkForLoadFormValues(document)),
  clearFormValues: (id: string): void => dispatch(actions.clearFormValues(id)),
  unsetEditing: (id: string): void => dispatch(actions.unsetEditing(id)),

  createDocument: (name: string, noFormUpdate?: boolean): string =>
    dispatch(actionsD.createDocument(name, noFormUpdate)),
  createCommit: (editingDocument: IEditingDocument, oldData: TAnyObject & IControlProps): string =>
    dispatch(actionsD.createCommit(editingDocument, oldData)),
  updateDocument: (editingDocument: IEditingDocument, formData: TAnyObject & IControlProps): string =>
    dispatch(actionsD.updateDocumentData(editingDocument, formData)),
  closeCommit: (editingDocument: IEditingDocument, formData: TAnyObject & IControlProps): void =>
    dispatch(actionsD.closeCommit(editingDocument, formData)),
  deleteCommit: (editingDocument: IEditingDocument): void => dispatch(actionsD.deleteCommit(editingDocument)),
  deleteDocument: (editingDocument: IEditingDocument, reason: { [key: string]: string | PartialDate }): void =>
    dispatch(actionsD.deleteDocument(editingDocument, reason)),
});

interface IOwnProps {
  name: string;
  parentDate?: PartialDate | null;
  onChange: IFormData['onChange'];
  document?: IDocumentWithTypeV2<'updrs_iii'>;
  viewing?: boolean;
  context: string;
  retro?: boolean;
  testType?: 'UPDRS' | 'MDSUPDRS';
}

export default connect<TAnyObject, IDispatchFromProps, IOwnProps>(null, mapDispatchToProps)(UPDRSDialog);
